import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useStripeConnection } from "../hooks/stripeQueries";
import { useStripeConnect } from "../hooks/useStripeConnect";

const states = {
  not_connected: ["Not connected", "Set up a US business Stripe account with a full Stripe Dashboard. Your connection is shared across this business."],
  setup_incomplete: ["Setup incomplete", "Continue the hosted setup for your existing account. You do not need to create another account."],
  verification_pending: ["Verification pending", "Stripe is reviewing your information. Verification is not complete yet. Refresh later to check for updates."],
  connected: ["Connected", "Your Stripe account is connected. Manage your account in the full Stripe Dashboard."],
  action_required: ["Action required", "Stripe needs more information. Continue setup for your existing account."],
  connection_removed: ["Connection removed", "Contact Froppio support to recover this connection. Do not create a replacement account."],
} as const;

function StripeSettings({ businessId, locationId, userId }: { businessId: string; locationId: string; userId: string }) {
  const { state } = useLocation();
  const outcome = state?.stripeOutcome;
  const query = useStripeConnection(businessId, userId);
  const flow = useStripeConnect(businessId, locationId, userId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const connection = query.data;
  return <Page><PageHeader title="Stripe" /><PageContent>
    <Section title="Stripe connection" card>
      <p className="text-sm text-muted-foreground">Connecting Stripe does not enable charging customers in Froppio yet.</p>
      {outcome === "expired" && <p role="status">Your setup link expired. Check the status below and continue setup if needed.</p>}
      {outcome === "error" && <p role="status">Stripe setup could not finish. Check the status below before trying again.</p>}
      {outcome === "returned" && <p role="status">Returned from Stripe. The status below comes from Froppio's account check, not from the return link.</p>}
      {query.isPending ? <LoadingSpinner message="Loading Stripe connection..." /> : query.error ?
        <ErrorState title="Couldn't load Stripe connection" message="Connection status is unavailable. Please try again." onRetry={() => void query.refetch()} /> :
        connection && <>
          <p className="font-medium">{states[connection.status][0]}</p>
          <p className="text-sm text-muted-foreground">{states[connection.status][1]}</p>
          <p className="text-sm text-muted-foreground">Environment: {connection.environment === "test" ? "Test" : "Live"}</p>
          {connection.account && <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt>Business</dt><dd>{connection.account.businessName ?? "Stripe business account"}</dd>
            <dt>Account ID</dt><dd className="break-all">{connection.account.id}</dd>
            <dt>Country</dt><dd>{connection.account.country}</dd>
            <dt>Currency</dt><dd>{connection.account.currency ?? "Not provided"}</dd>
          </dl>}
          <p className="text-sm text-muted-foreground">{connection.lastSyncedAt ? `Last synced: ${connection.lastSyncedAt.toLocaleString()}` : "Status has not been synced yet."}</p>
          <div className="flex flex-wrap gap-2">
            {["not_connected", "setup_incomplete", "action_required"].includes(connection.status) &&
              <Button disabled={flow.busy} onClick={() => connection.status === "not_connected" ? setConfirmOpen(true) : void flow.connect()}>
                {flow.busy ? "Opening Stripe..." : connection.status === "not_connected" ? "Connect Stripe" : "Continue setup"}
              </Button>}
            {connection.account && connection.status !== "connection_removed" &&
              <Button variant="outline" asChild><a href="https://dashboard.stripe.com/" target="_blank" rel="noopener noreferrer">Open Stripe Dashboard</a></Button>}
          </div>
        </>}
      <Button variant="outline" disabled={query.isFetching} onClick={() => void query.refetch()}>Refresh status</Button>
      {flow.message && <p role="status" className="text-sm">{flow.message}</p>}
    </Section>
    <ConfirmationDialog open={confirmOpen} onOpenChange={setConfirmOpen}
      title="Confirm your business country" description="This setup is for a United States business account. Confirm that your business is based in the US. Stripe collects and verifies your business details in your browser, not in Froppio."
      confirmLabel="My business is in the US" dismissLabel="Cancel"
      onConfirm={() => { setConfirmOpen(false); void flow.connect(); }} />
  </PageContent></Page>;
}

export function StripePage() {
  const { user } = useAuth();
  const { business, businessId, loading } = useCurrentBusiness();
  const { locationId } = useParams();
  if (!loading && business && businessId && locationId && user && business.ownerId === user.id) {
    return <StripeSettings key={`${businessId}:${locationId}:${user.id}`} businessId={businessId} locationId={locationId} userId={user.id} />;
  }
  return <Page><PageHeader title="Stripe" /><PageContent>
    {loading ? <LoadingSpinner message="Loading business..." /> :
      <ErrorState message={business ? "Only the business owner can manage Stripe." : "Select a business to manage Stripe."} />}
  </PageContent></Page>;
}
